import { useEffect, useMemo, useRef, useState } from 'react';
import {
    useLazyGetClaireDashboardQuery,
    useLazyGetClaireCallByIdQuery,
    useRefreshClaireCallFromVapiMutation,
} from '../../../../../store/excecutive/executiveApi';
import {
    IClaireDemoCallDetailsResponse,
    IClaireDemoDashboardCall,
    IClaireDemoTranscriptItem,
} from '../../../../../interfaces/excecutive/IClaireDemoDashboard';
import { DEVICE_INFO_API_URL } from '../../../../../config';

/**
 * Vapi recordings now sit behind an authenticated endpoint (Bearer private key -> 302 to a
 * short-lived signed URL) instead of the permanent recordingUrl this dashboard used to read
 * straight off call records. This backend route holds the private key server-side and has
 * no app auth guard (gated only by the unguessable Vapi callId), so it can be used as a
 * plain <audio src>/<a href> URL — the browser follows the redirect and does native Range
 * requests, which is what makes duration metadata and table-row probing cheap. Do NOT
 * eagerly fetch this as a blob for playback/probing; only do that for explicit downloads.
 */
const buildRecordingUrl = (callId?: string | null, hasRecording?: boolean) =>
    callId && hasRecording
        ? `${DEVICE_INFO_API_URL}/claire-demo/calls/${callId}/recording`
        : null;

const isSystemTranscriptEntry = (speaker?: string) =>
    (speaker || '').trim().toLowerCase() === 'system';

const getVisibleTranscript = (transcript?: IClaireDemoTranscriptItem[]) =>
    Array.isArray(transcript)
        ? transcript.filter((entry) => !isSystemTranscriptEntry(entry.speaker))
        : [];

const formatDate = (value?: string) => {
    if (!value) return 'N/A';
    return new Date(value).toLocaleString();
};

const formatSeconds = (value?: number) => {
    const total = Number(value || 0);
    const minutes = Math.floor(total / 60);
    const seconds = Math.floor(total % 60);
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/** First finite value greater than 0, or undefined. */
const pickPositiveNumber = (...vals: unknown[]): number | undefined => {
    for (const v of vals) {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) {
            return n;
        }
    }
    return undefined;
};

const getServerRecordingDurationSec = (call: Record<string, unknown>) =>
    pickPositiveNumber(
        call.recordingDurationSec,
        call.recording_duration_sec,
        call.recordingDurationSeconds,
        call.mediaDurationSec,
        call.audioDurationSec,
    );

const CallTableDurationCell = ({ call }: { call: IClaireDemoDashboardCall }) => {
    const ext = call as IClaireDemoDashboardCall & {
        recording_url?: string | null;
    };
    const recordingUrlRaw = ext.recordingUrl ?? ext.recording_url ?? null;
    const hasStoredRecordingUrl =
        typeof recordingUrlRaw === 'string' && recordingUrlRaw.trim().length > 0;

    const serverRecordingSec = getServerRecordingDurationSec(
        call as unknown as Record<string, unknown>,
    );

    const shouldProbe = serverRecordingSec == null && hasStoredRecordingUrl;
    const probeUrl = buildRecordingUrl(call.callId, shouldProbe);

    const [probedSec, setProbedSec] = useState<number | false | null>(null);

    useEffect(() => {
        setProbedSec(null);
        if (serverRecordingSec != null) {
            setProbedSec(false);
            return;
        }
        if (!probeUrl) {
            return;
        }

        const audio = new Audio();
        audio.preload = 'metadata';

        const onLoaded = () => {
            const d = audio.duration;
            if (Number.isFinite(d) && d > 0 && !Number.isNaN(d)) {
                setProbedSec(d);
            } else {
                setProbedSec(false);
            }
        };

        const onError = () => setProbedSec(false);

        audio.addEventListener('loadedmetadata', onLoaded);
        audio.addEventListener('error', onError);
        audio.src = probeUrl;

        return () => {
            audio.removeEventListener('loadedmetadata', onLoaded);
            audio.removeEventListener('error', onError);
            audio.removeAttribute('src');
            audio.load();
        };
    }, [call.callId, probeUrl, serverRecordingSec]);

    const fromServer = serverRecordingSec;
    if (fromServer != null) {
        return <>{formatSeconds(fromServer)}</>;
    }

    if (hasStoredRecordingUrl) {
        if (probedSec === null) {
            return <span className="text-gray-400 dark:text-gray-500">…</span>;
        }
        if (typeof probedSec === 'number' && probedSec > 0) {
            return <>{formatSeconds(probedSec)}</>;
        }
    }

    if (!call.hasRecording && !hasStoredRecordingUrl) {
        return <>N/A</>;
    }

    const fallback = Number(call.callDurationSec || 0);
    return <>{fallback > 0 ? formatSeconds(fallback) : 'N/A'}</>;
};

const formatShortCallId = (callId?: string) => {
    if (!callId) return 'N/A';
    const normalized = String(callId).trim();
    return (normalized.length > 8 ? normalized.slice(-8) : normalized).toUpperCase();
};

const PAGE_SIZE = 10;
const CALLS_TABLE_POLL_MS = 7000;
type CallsFilter = 'all' | 'transferred' | 'successful' | 'failed' | 'abandoned';

const CALLS_FILTERS: { label: string; value: CallsFilter }[] = [
    { label: 'All Calls', value: 'all' },
    { label: 'Transferred', value: 'transferred' },
    { label: 'Successful', value: 'successful' },
    { label: 'Failed', value: 'failed' },
    { label: 'Abandoned', value: 'abandoned' },
];

const extractCalls = (payload: any): IClaireDemoDashboardCall[] => {
    if (Array.isArray(payload?.calls)) return payload.calls;
    if (Array.isArray(payload?.calls?.items)) return payload.calls.items;
    if (Array.isArray(payload?.calls?.elements)) return payload.calls.elements;
    if (Array.isArray(payload?.data?.calls)) return payload.data.calls;
    if (Array.isArray(payload?.data?.calls?.items)) return payload.data.calls.items;
    if (Array.isArray(payload?.items)) return payload.items;
    return [];
};

const extractTotalCalls = (payload: any): number => {
    const total = Number(
        payload?.pagination?.total ??
            payload?.calls?.total ??
            payload?.meta?.total ??
            payload?.total ??
            0,
    );
    return Number.isFinite(total) ? total : 0;
};

const extractHasNextPage = (payload: any): boolean | undefined => {
    const value =
        payload?.pagination?.hasNextPage ??
        payload?.calls?.hasNextPage ??
        payload?.meta?.hasNextPage ??
        payload?.hasNextPage;
    return typeof value === 'boolean' ? value : undefined;
};

const boolPill = (value?: boolean) => (
    <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            value
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
        }`}>
        {value ? 'Yes' : 'No'}
    </span>
);

const RecordingWaveform = ({
    recordingUrl,
    onDurationChange,
}: {
    recordingUrl?: string | null;
    onDurationChange?: (duration: number) => void;
}) => {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const waveRef = useRef<HTMLDivElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    const [topSamples, bottomSamples] = useMemo(() => {
        const size = 240;
        const seed = (recordingUrl || 'recording')
            .split('')
            .reduce((acc, char, idx) => acc + char.charCodeAt(0) * (idx + 1), 0);

        const top = Array.from({ length: size }, (_, index) => {
            const waveA = Math.abs(Math.sin((index + seed) * 0.14));
            const waveB = Math.abs(Math.cos((index * 1.3 + seed) * 0.09));
            const jitter = Math.sin((index + seed) * 0.31) * 0.12;
            return (waveA * 0.55 + waveB * 0.45 + jitter) * 20;
        });

        const bottom = Array.from({ length: size }, (_, index) => {
            const waveA = Math.abs(Math.cos((index + seed) * 0.11));
            const waveB = Math.abs(Math.sin((index * 1.8 + seed) * 0.05));
            const jitter = Math.cos((index + seed) * 0.27) * 0.1;
            return (waveA * 0.5 + waveB * 0.5 + jitter) * 22;
        });

        return [top, bottom];
    }, [recordingUrl]);

    const clipIdBase = useMemo(() => {
        const hash = (recordingUrl || 'recording')
            .split('')
            .reduce((acc, char, idx) => acc + char.charCodeAt(0) * (idx + 1), 0);
        return `wave-${Math.abs(hash)}`;
    }, [recordingUrl]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onLoaded = () => {
            const nextDuration = audio.duration || 0;
            setDuration(nextDuration);
            onDurationChange?.(nextDuration);
        };
        const onTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
        const onEnded = () => setIsPlaying(false);

        audio.addEventListener('loadedmetadata', onLoaded);
        audio.addEventListener('timeupdate', onTimeUpdate);
        audio.addEventListener('ended', onEnded);

        // The element (and its src) may not have existed yet on a previous render
        // (e.g. recordingUrl was still null) — if metadata is already available by
        // the time we attach, this effect's own listener registration above would
        // otherwise miss it.
        if (audio.readyState >= 1) {
            onLoaded();
        }

        return () => {
            audio.removeEventListener('loadedmetadata', onLoaded);
            audio.removeEventListener('timeupdate', onTimeUpdate);
            audio.removeEventListener('ended', onEnded);
        };
    }, [recordingUrl, onDurationChange]);

    useEffect(() => {
        if (!recordingUrl) {
            onDurationChange?.(0);
        }
    }, [recordingUrl, onDurationChange]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.play().catch(() => {
                setIsPlaying(false);
            });
        } else {
            audio.pause();
        }
    }, [isPlaying]);

    if (!recordingUrl) {
        return <p className="text-sm dark:text-gray-200">No recording available</p>;
    }

    const progressRatio = duration > 0 ? Math.min(1, Math.max(0, currentTime / duration)) : 0;
    const tickCount = duration > 0 ? Math.min(8, Math.max(4, Math.floor(duration / 8))) : 6;
    const viewWidth = 1000;
    const viewHeight = 100;
    const topBaseY = 28;
    const bottomBaseY = 72;
    const playHeadX = progressRatio * viewWidth;

    const buildWavePath = (
        samples: number[],
        baselineY: number,
        multiplier: number,
    ) => {
        if (!samples.length) return '';

        return samples
            .map((sample, index) => {
                const x = (index / (samples.length - 1)) * viewWidth;
                const y = baselineY + Math.sin(index * 0.25) * sample * multiplier;
                return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
            })
            .join(' ');
    };

    const topPath = buildWavePath(topSamples, topBaseY, -0.35);
    const bottomPath = buildWavePath(bottomSamples, bottomBaseY, 0.32);

    const onClickWave = (event: any) => {
        if (!waveRef.current || !duration || !audioRef.current) return;
        const rect = waveRef.current.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const ratio = Math.max(0, Math.min(1, x / rect.width));
        const nextTime = ratio * duration;
        audioRef.current.currentTime = nextTime;
        setCurrentTime(nextTime);
    };

    return (
        <div className="rounded-xl bg-[#090D14] p-4 border border-[#1F2937]">
            <audio ref={audioRef} src={recordingUrl} preload="metadata" />
            <div className="flex items-center justify-between mb-3 gap-3">
                <button
                    onClick={() => setIsPlaying((value) => !value)}
                    className="inline-flex items-center justify-center rounded-md bg-[#111827] border border-[#1F2937] text-cyan-300 text-xs font-semibold px-2.5 py-1.5 hover:bg-[#182234]">
                    {isPlaying ? 'Pause' : 'Play'}
                </button>
                <p className="text-[11px] text-gray-400 ml-auto">
                    {formatSeconds(currentTime)} / {formatSeconds(duration)}
                </p>
            </div>
            <div
                ref={waveRef}
                onClick={onClickWave}
                className="relative h-20 rounded-md bg-[#0B1220] border border-[#1F2937] cursor-pointer overflow-hidden">
                <svg
                    viewBox={`0 0 ${viewWidth} ${viewHeight}`}
                    preserveAspectRatio="none"
                    className="w-full h-full">
                    <defs>
                        <clipPath id={`${clipIdBase}-top`}>
                            <rect
                                x="0"
                                y="0"
                                width={playHeadX}
                                height={viewHeight}
                            />
                        </clipPath>
                        <clipPath id={`${clipIdBase}-bottom`}>
                            <rect
                                x="0"
                                y="0"
                                width={playHeadX}
                                height={viewHeight}
                            />
                        </clipPath>
                    </defs>

                    <line
                        x1="0"
                        y1={topBaseY}
                        x2={viewWidth}
                        y2={topBaseY}
                        stroke="rgba(245, 158, 11, 0.75)"
                        strokeWidth="1"
                    />
                    <line
                        x1="0"
                        y1={bottomBaseY}
                        x2={viewWidth}
                        y2={bottomBaseY}
                        stroke="rgba(34, 211, 238, 0.75)"
                        strokeWidth="1"
                    />

                    <path
                        d={topPath}
                        fill="none"
                        stroke="rgba(180, 83, 9, 0.45)"
                        strokeWidth="1.3"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                    />
                    <path
                        d={topPath}
                        fill="none"
                        stroke="#f59e0b"
                        strokeWidth="1.8"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        clipPath={`url(#${clipIdBase}-top)`}
                    />

                    <path
                        d={bottomPath}
                        fill="none"
                        stroke="rgba(8, 145, 178, 0.4)"
                        strokeWidth="1.3"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                    />
                    <path
                        d={bottomPath}
                        fill="none"
                        stroke="#22d3ee"
                        strokeWidth="1.8"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        clipPath={`url(#${clipIdBase}-bottom)`}
                    />

                    <line
                        x1={playHeadX}
                        y1="0"
                        x2={playHeadX}
                        y2={viewHeight}
                        stroke="rgba(255,255,255,0.7)"
                        strokeWidth="1"
                    />
                </svg>
            </div>
            <div className="mt-2 flex items-center justify-between">
                {Array.from({ length: tickCount + 1 }).map((_, index) => {
                    const tickTime = duration > 0 ? (duration / tickCount) * index : index * 5;
                    return (
                        <span key={`tick-${index}`} className="text-[10px] text-gray-500">
                            {formatSeconds(tickTime)}
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

const CallDetailsModal = ({
    open,
    onClose,
    loading,
    recovering,
    error,
    missingAfterRecovery,
    data,
    fallbackFromNumber,
    onRetryRecovery,
}: {
    open: boolean;
    onClose: () => void;
    loading: boolean;
    recovering: boolean;
    error: boolean;
    missingAfterRecovery: boolean;
    data?: IClaireDemoCallDetailsResponse;
    fallbackFromNumber?: string;
    onRetryRecovery: () => void;
}) => {
    const [recordingDurationSec, setRecordingDurationSec] = useState(0);
    const recordingUrl = buildRecordingUrl(
        data?.callId,
        Boolean(data?.recordingUrl),
    );
    const fromNumberValue =
        (data as any)?.fromNumber ||
        (data as any)?.from_number ||
        (data as any)?.call?.fromNumber ||
        fallbackFromNumber;
    const toNumberValue =
        (data as any)?.toNumber ||
        (data as any)?.to_number ||
        (data as any)?.call?.toNumber;

    useEffect(() => {
        setRecordingDurationSec(0);
    }, [data?.callId]);

    const visibleTranscript = useMemo(
        () => getVisibleTranscript(data?.transcript),
        [data?.transcript],
    );

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/75 backdrop-blur-[2px]">
            <div className="w-full md:w-[700px] h-full overflow-y-auto bg-white dark:bg-[#050505] p-5 shadow-2xl">
                <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-200 dark:border-white/10">
                    <h3 className="text-xl font-semibold dark:text-white">
                        Call Details
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-sm text-gray-600 dark:text-gray-200 underline">
                        Close
                    </button>
                </div>

                {loading ? (
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        Loading details...
                    </p>
                ) : recovering ? (
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                        Fetching transcript/recording from provider...
                    </p>
                ) : error ? (
                    <p className="text-sm text-red-600 dark:text-red-400">
                        Failed to load call details
                    </p>
                ) : !data ? (
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        No call details found
                    </p>
                ) : (
                    <>
                        <div className="rounded-xl border p-4 mb-4 border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#0B0B0B]">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">
                                Summary
                            </p>
                            <p className="text-sm leading-6 dark:text-gray-100">
                                {data.summary || 'No summary'}
                            </p>
                        </div>

                        <div className="rounded-xl border p-4 mb-4 border-gray-200 dark:border-white/10 dark:bg-[#0B0B0B]">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wide">
                                Recording
                            </p>
                            <RecordingWaveform
                                recordingUrl={recordingUrl}
                                onDurationChange={setRecordingDurationSec}
                            />
                        </div>

                        <div className="rounded-xl border p-4 mb-4 border-gray-200 dark:border-white/10 dark:bg-[#0B0B0B]">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wide">
                                Metadata
                            </p>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div className="rounded-md bg-gray-50 dark:bg-[#0F0F0F] p-2">
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                        Call ID
                                    </p>
                                    <p className="dark:text-gray-100 font-mono font-bold break-all">
                                        {formatShortCallId(data.callId)}
                                    </p>
                                </div>
                                <div className="rounded-md bg-gray-50 dark:bg-[#0F0F0F] p-2">
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                        Assistant
                                    </p>
                                    <p className="dark:text-gray-100">
                                        {data.assistantName || 'N/A'}
                                    </p>
                                </div>
                                <div className="rounded-md bg-gray-50 dark:bg-[#0F0F0F] p-2">
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                        Scenario
                                    </p>
                                    <p className="dark:text-gray-100">
                                        {data.scenario || 'N/A'}
                                    </p>
                                </div>
                                <div className="rounded-md bg-gray-50 dark:bg-[#0F0F0F] p-2">
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                        Outcome
                                    </p>
                                    <p className="dark:text-gray-100">
                                        {data.outcome || 'N/A'}
                                    </p>
                                </div>
                                <div className="rounded-md bg-gray-50 dark:bg-[#0F0F0F] p-2">
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                        Duration
                                    </p>
                                    <p className="dark:text-gray-100">
                                        {(() => {
                                            const fromApi = pickPositiveNumber(
                                                (data as any).recordingDurationSec,
                                                (data as any).recording_duration_sec,
                                            );
                                            const fromPlayer =
                                                recordingDurationSec > 0
                                                    ? recordingDurationSec
                                                    : undefined;
                                            const fromCall = pickPositiveNumber(
                                                data.callDurationSec,
                                            );
                                            const sec =
                                                fromApi ?? fromPlayer ?? fromCall;
                                            return sec != null
                                                ? formatSeconds(sec)
                                                : 'N/A';
                                        })()}
                                    </p>
                                </div>
                                <div className="rounded-md bg-gray-50 dark:bg-[#0F0F0F] p-2">
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                        Updated At
                                    </p>
                                    <p className="dark:text-gray-100">
                                        {formatDate(data.updatedAt)}
                                    </p>
                                </div>
                                {fromNumberValue && (
                                    <div className="rounded-md bg-gray-50 dark:bg-[#0F0F0F] p-2">
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                            From Number
                                        </p>
                                        <p className="dark:text-gray-100 break-all">
                                            {fromNumberValue}
                                        </p>
                                    </div>
                                )}
                                {toNumberValue && (
                                    <div className="rounded-md bg-gray-50 dark:bg-[#0F0F0F] p-2">
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                            Inbound Call Numer
                                        </p>
                                        <p className="dark:text-gray-100 break-all">
                                            {toNumberValue}
                                        </p>
                                    </div>
                                )}
                                <div className="rounded-md bg-gray-50 dark:bg-[#0F0F0F] p-2">
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">
                                        Success
                                    </p>
                                    {boolPill(data.success)}
                                </div>
                                <div className="rounded-md bg-gray-50 dark:bg-[#0F0F0F] p-2">
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">
                                        Handoff
                                    </p>
                                    {boolPill(data.handoff)}
                                </div>
                                <div className="rounded-md bg-gray-50 dark:bg-[#0F0F0F] p-2">
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">
                                        Booking Conversion
                                    </p>
                                    {boolPill(data.bookingConversion)}
                                </div>
                                <div className="rounded-md bg-gray-50 dark:bg-[#0F0F0F] p-2">
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">
                                        Tool Failure
                                    </p>
                                    {boolPill(data.toolFailure)}
                                </div>
                            </div>
                        </div>

                        {missingAfterRecovery && (
                            <div className="rounded-xl border p-4 mb-4 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-900/40">
                                <p className="text-sm text-amber-800 dark:text-amber-200">
                                    Transcript/recording not available yet.
                                </p>
                                <button
                                    onClick={onRetryRecovery}
                                    className="mt-2 text-xs underline text-amber-900 dark:text-amber-200">
                                    Retry fetch from provider
                                </button>
                            </div>
                        )}

                        <div className="rounded-xl border p-4 dark:border-gray-700">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wide">
                                Transcript
                            </p>
                            {visibleTranscript.length === 0 ? (
                                <p className="text-sm dark:text-gray-200">
                                    No transcript available
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {visibleTranscript.map((entry, index) => {
                                        const normalizedSpeaker = (
                                            entry.speaker || ''
                                        ).toLowerCase();
                                        const isAssistantSpeaker =
                                            normalizedSpeaker.includes(
                                                'assistant',
                                            ) ||
                                            normalizedSpeaker === 'ai' ||
                                            normalizedSpeaker === 'bot' ||
                                            normalizedSpeaker === 'claire';
                                        const speakerLabel = isAssistantSpeaker
                                            ? 'Claire'
                                            : entry.speaker;

                                        return (
                                        <div
                                            key={`${entry.speaker}-${index}`}
                                            className={`rounded-md p-2 border ${
                                                isAssistantSpeaker
                                                    ? 'bg-cyan-50 border-cyan-100 dark:bg-cyan-900/15 dark:border-cyan-900/40'
                                                    : 'bg-amber-50 border-amber-100 dark:bg-amber-900/15 dark:border-amber-900/40'
                                            }`}>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                                {speakerLabel}
                                                {entry.timestamp
                                                    ? ` - ${new Date(
                                                          entry.timestamp,
                                                      ).toLocaleString()}`
                                                    : ''}
                                            </p>
                                            <p className="text-sm dark:text-gray-200">
                                                {entry.text}
                                            </p>
                                        </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const SpaAnalyticsTest = ({
    startDate,
    endDate,
    tableOnly = false,
    initialFilter = 'all',
}: {
    startDate?: string;
    endDate?: string;
    tableOnly?: boolean;
    initialFilter?: CallsFilter;
}) => {
    const [selectedCallId, setSelectedCallId] = useState<string>('');
    const [showDetails, setShowDetails] = useState(false);
    const [callDetails, setCallDetails] = useState<IClaireDemoCallDetailsResponse>();
    const [isCallLoading, setIsCallLoading] = useState(false);
    const [isCallError, setIsCallError] = useState(false);
    const [isRecoveringFromProvider, setIsRecoveringFromProvider] = useState(false);
    const [missingAfterRecovery, setMissingAfterRecovery] = useState(false);
    const [calls, setCalls] = useState<IClaireDemoDashboardCall[]>([]);
    const [selectedCallFromNumber, setSelectedCallFromNumber] = useState<string | undefined>();
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreCalls, setHasMoreCalls] = useState(true);
    const [isTableLoading, setIsTableLoading] = useState(true);
    const [isLoadingMoreCalls, setIsLoadingMoreCalls] = useState(false);
    const [isTableError, setIsTableError] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState<CallsFilter>(initialFilter);
    const [getClaireDashboard] = useLazyGetClaireDashboardQuery();
    const [getClaireCallById] = useLazyGetClaireCallByIdQuery();
    const [refreshFromVapi] = useRefreshClaireCallFromVapiMutation();

    const loadCalls = async (
        page: number,
        append: boolean,
        silentRefetch = false,
    ) => {
        if (append) {
            setIsLoadingMoreCalls(true);
        } else if (!silentRefetch) {
            setIsTableLoading(true);
        }
        setIsTableError(false);

        try {
            const response = await getClaireDashboard(
                {
                    page,
                    limit: PAGE_SIZE,
                    filter: selectedFilter,
                    ...(selectedFilter !== 'all' ? { status: selectedFilter } : {}),
                    ...(startDate ? { startDate } : {}),
                    ...(endDate ? { endDate } : {}),
                },
                false,
            ).unwrap();

            const nextCalls = extractCalls(response);
            const explicitHasNextPage = extractHasNextPage(response);
            const totalFromApi = extractTotalCalls(response);

            setCalls((previous) => {
                if (!append) return nextCalls;

                const seen = new Set(previous.map((item) => item.callId));
                const uniqueNext = nextCalls.filter(
                    (item) => !seen.has(item.callId),
                );
                return [...previous, ...uniqueNext];
            });

            const loadedCount = append ? calls.length + nextCalls.length : nextCalls.length;

            if (explicitHasNextPage !== undefined) {
                setHasMoreCalls(explicitHasNextPage);
            } else if (totalFromApi > 0) {
                setHasMoreCalls(loadedCount < totalFromApi);
            } else {
                setHasMoreCalls(nextCalls.length === PAGE_SIZE);
            }

            setCurrentPage(page);
        } catch (error) {
            setIsTableError(true);
        } finally {
            setIsLoadingMoreCalls(false);
            if (!silentRefetch) {
                setIsTableLoading(false);
            }
        }
    };

    const loadCallsRef = useRef(loadCalls);
    loadCallsRef.current = loadCalls;

    useEffect(() => {
        const id = window.setInterval(() => {
            loadCallsRef.current(1, false, true).catch(() => {
                /* errors handled inside loadCalls */
            });
        }, CALLS_TABLE_POLL_MS);
        return () => window.clearInterval(id);
    }, []);

    useEffect(() => {
        setCalls([]);
        setCurrentPage(1);
        setHasMoreCalls(true);
        loadCalls(1, false);
    }, [startDate, endDate, selectedFilter]);

    useEffect(() => {
        setSelectedFilter(initialFilter);
    }, [initialFilter]);

    const needsRecovery = (details?: IClaireDemoCallDetailsResponse) => {
        const hasTranscript =
            getVisibleTranscript(details?.transcript).length > 0;
        const hasRecording = Boolean(details?.recordingUrl);
        return !hasTranscript || !hasRecording;
    };

    const runCallDetailsFlow = async (callId: string, forceRefresh = false) => {
        if (!callId) return;
        setIsCallLoading(true);
        setIsCallError(false);
        setMissingAfterRecovery(false);
        try {
            let details = await getClaireCallById(callId, true).unwrap();

            if (forceRefresh || needsRecovery(details)) {
                setIsRecoveringFromProvider(true);
                await refreshFromVapi(callId).unwrap();
                details = await getClaireCallById(callId, true).unwrap();
                setIsRecoveringFromProvider(false);
            }

            setCallDetails(details);
            setMissingAfterRecovery(needsRecovery(details));
        } catch (error) {
            setIsCallError(true);
        } finally {
            setIsRecoveringFromProvider(false);
            setIsCallLoading(false);
        }
    };

    useEffect(() => {
        if (!showDetails || !selectedCallId) return;
        runCallDetailsFlow(selectedCallId);
    }, [showDetails, selectedCallId]);

    const openDetails = (callId: string, fromNumber?: string | null) => {
        setSelectedCallId(callId);
        setSelectedCallFromNumber(fromNumber || undefined);
        setShowDetails(true);
    };

    const closeDetails = () => {
        setShowDetails(false);
        setSelectedCallId('');
        setSelectedCallFromNumber(undefined);
        setCallDetails(undefined);
        setIsCallError(false);
        setMissingAfterRecovery(false);
    };

    return (
        <div
            className={`w-full border border-gray-200 rounded-2xl p-4 md:p-6 my-5 dark:bg-[#1F1F1F] dark:border-transparent ${
                tableOnly ? 'mt-3' : ''
            }`}>
            <CallDetailsModal
                open={showDetails}
                onClose={closeDetails}
                loading={isCallLoading}
                recovering={isRecoveringFromProvider}
                error={Boolean(isCallError)}
                data={callDetails}
                fallbackFromNumber={selectedCallFromNumber}
                missingAfterRecovery={missingAfterRecovery}
                onRetryRecovery={() => runCallDetailsFlow(selectedCallId, true)}
            />

            {!tableOnly && (
                <div className="w-full flex flex-wrap mb-2">
                    <div className="w-full md:w-9/12 mb-2">
                        <h4 className="text-[#1C1C1C] dark:text-primary-dark font-semibold text-sm mb-1">
                            Claire Demo Dashboard
                        </h4>
                        <p className="text-xs text-gray-800 dark:text-white">
                            Failures, calls, and row-level call details
                        </p>
                    </div>
                </div>
            )}

            <div className="w-full mb-8">
                <h5 className="text-[#1C1C1C] dark:text-primary-dark font-semibold text-sm mb-4">
                    Calls
                </h5>
                <div className="mb-3 flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
                    {CALLS_FILTERS.map((item) => (
                        <button
                            key={item.value}
                            type="button"
                            onClick={() => setSelectedFilter(item.value)}
                            className={`px-2 py-1 text-sm transition-colors ${
                                selectedFilter === item.value
                                    ? 'text-[#00330F] dark:text-emerald-300 border-b-2 border-[#00330F] dark:border-emerald-300 font-medium'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                            }`}>
                            {item.label}
                        </button>
                    ))}
                </div>
                <div
                    className="w-full rounded-2xl border min-h-20 dark:border-gray-700 overflow-auto h-[480px]"
                    onScroll={(event) => {
                        const target = event.currentTarget;
                        const nearBottom =
                            target.scrollTop + target.clientHeight >=
                            target.scrollHeight - 80;

                        if (
                            nearBottom &&
                            hasMoreCalls &&
                            !isLoadingMoreCalls &&
                            !isTableLoading
                        ) {
                            loadCalls(currentPage + 1, true);
                        }
                    }}>
                    {isTableLoading ? (
                        <div className="flex items-center justify-center h-40">
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                Loading calls...
                            </p>
                        </div>
                    ) : isTableError ? (
                        <div className="flex items-center justify-center h-40">
                            <p className="text-red-600 dark:text-red-400 text-sm">
                                Failed to load calls
                            </p>
                        </div>
                    ) : !calls.length ? (
                        <div className="flex items-center justify-center h-40">
                            <p className="text-gray-500 dark:text-gray-400">
                                No calls available
                            </p>
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-[#333333] sticky top-0 z-10">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        Call ID
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        Assistant
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        Summary
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        Duration
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        Handoff
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        Booking Conv.
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        Tool Failure
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        Has Recording
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        Has Transcript
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        Updated
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-[#1B1B1B] divide-y divide-gray-200 dark:divide-gray-700">
                                {calls.map((call) => (
                                    <tr
                                        key={call.callId}
                                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-[#2A2A2A]"
                                        onClick={() => openDetails(call.callId, call.fromNumber)}>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-bold font-mono">
                                            {formatShortCallId(call.callId)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                            {call.assistantName || 'N/A'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[320px]">
                                            <p className="line-clamp-2">
                                                {call.summary || 'No summary'}
                                            </p>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                            <CallTableDurationCell call={call} />
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                            {boolPill(call.handoff)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                            {boolPill(call.bookingConversion)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                            {boolPill(call.toolFailure)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                            {boolPill(call.hasRecording)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                            {boolPill(call.hasTranscript)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                                            {formatDate(call.updatedAt)}
                                        </td>
                                    </tr>
                                ))}
                                {isLoadingMoreCalls && (
                                    <tr>
                                        <td
                                            colSpan={10}
                                            className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                                            Loading more calls...
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

        </div>
    );
};

export default SpaAnalyticsTest;
